// Invoice reconcile service — candidate fetching, locking, and batch orchestration.
// Composes InvoiceDao + CompanyService. No raw Supabase calls except for RPC locks.

import { InvoiceDao, type ReconcileWritePayload } from './InvoiceDao.ts';
import { CompanyService } from '../company/CompanyService.ts';
import { getVendorId, candidateToTransactionSide } from './utils.ts';
import { applyCorrections } from './effectiveLineItems.ts';
import { matchInvoiceBatch, type BatchMatchInput } from './matching/matchEngine.ts';
import { assignReferencesToLines } from './matching/assignReferences.ts';
import { buildReconcileReport, buildNoopReconcileReport } from './reportBuilder.ts';
import { isInvoicePaid } from './paymentState.ts';
import type { ErrorReporter } from '../reporter.ts';
import { consoleErrorReporter } from '../reporter.ts';
import type { MatchLineItem, MatchResult } from './matching/types.ts';
import type { SupabaseClient, InvalidateFn } from '../base.ts';
import type {
  InvoiceRow,
  InvoiceLineRow,
  InvoiceLinkInsert,
  InvoiceLink,
} from '@shared/types/invoice.ts';
import type { EffectiveLineItem } from './effectiveLineItems.ts';
import type { InvoiceDirection } from '../../types.ts';

// ── Reconcile-internal types ────────────────────────────────────────────

export type TransactionSideType = 'load' | 'unload' | 'transport';

export interface TransactionSide {
  transaction_id: number;
  side: TransactionSideType;
  transport_id: number | null;
}

// Non-commercial cost documents (tax / ZUS / payroll) carry their own invoice_type
// (podatek / zus / wynagrodzenie, derived from the Fakturownia kind). The sweep already
// fetches only invoice_type='original', so they are excluded from matching for free — no
// separate category/kind filter needed.

// Already-paid invoices relax the reconcile date gate to an adjacent month (settled
// history, e.g. a quarterly straggler a month off); unpaid/on-hold/partial stay strict so
// cross-month discrepancies surface before payment. "Paid" is the canonical predicate
// `isInvoicePaid` (bank-settled `payment_status` OR Fakturownia) — shared with the
// approval + mBank layers so the definition never forks.

export interface ReconcilePreparedInvoice {
  invoice: InvoiceRow;
  effectiveLineItems: EffectiveLineItem[];
  originalLineItems: InvoiceLineRow[];
  correctionIds: number[];
  /** Sell date AFTER corrections — a correction can move the sale date (incl. to a
   *  different month). The same-month date gate must use this, not the original. */
  effectiveSellDate: string | null;
}

export interface ReconcileBatchContext {
  prepared: ReconcilePreparedInvoice[];
  candidatesByVendor: Map<number, InvoiceLink[]>;
  /** MAJOR-5: per-vendor ref-miss DIAGNOSIS pool = the priced candidate pool PLUS the
   *  unpriced sides `candidate_sides` excludes (so a ref naming an unpriced tx yields
   *  `ref_found_tx_unpriced` instead of a generic `p1_ref_unmatched`). Never fed to the
   *  working matcher — diagnosis only. Optional: when omitted, ref-miss diagnosis falls
   *  back to `candidatesByVendor` (the priced pool), i.e. the pre-MAJOR-5 behaviour. */
  refLookupByVendor?: Map<number, InvoiceLink[]>;
  familyMap: Map<number, number[]>;
}

export type ProcessBatchFn = (ctx: ReconcileBatchContext) => Promise<void>;

export interface BatchReconcileOptions {
  direction: 'received' | 'issued';
  batchSize?: number;
  invoiceId?: number;
  /** Cap the number of batches processed in this call (for workflow-driven chunking).
   *  When set, processed invoices are NOT touch-cleared and a LONG touch window
   *  (CHUNKED_TOUCH_TIMEOUT_MIN) keeps them skipped across the whole looped drain, so
   *  the pending pool empties instead of unmatched invoices re-entering each chunk.
   *  Default: unbounded (drain in one call, 2-min window). */
  maxBatches?: number;
}

/** Touch window for chunked (looped) reconcile — must exceed total drain wall-clock so a
 *  touched invoice stays skipped until the pool is empty (the 2-min default is too short
 *  once a multi-batch chunk runs longer than the window). */
const CHUNKED_TOUCH_TIMEOUT_MIN = 30;

export interface BatchReconcileResult {
  totalProcessed: number;
  batches: number;
  /** True when the call stopped at the maxBatches cap (more pending likely remain). */
  hasMore?: boolean;
}

// ── Result types ────────────────────────────────────────────────────────

/** One invoice's shaped match output, ready for bulk write. */
interface MatchWriteResult {
  invoiceId: number;
  correctionIds: number[];
  links: InvoiceLinkInsert[];
  reviewStatus: 'approved' | 'pending' | 'request_correction';
  report: unknown;
  /** Finding 7: this invoice entered the pool ALREADY approved (arrived approved from
   *  Fakturownia, never reconciled). reconcile_apply writes its links + report but must
   *  NOT overwrite its review_status — matching never demotes an approved invoice.
   *  Also set for an invoice that entered as 'request_correction' (see shapeResult). */
  preserveReviewStatus?: boolean;
  /** The status the invoice held on ENTRY. When preserveReviewStatus is set this — not
   *  `reviewStatus` — is what the invoice ends up with, so it decides whether the
   *  invoice's corrections are approved alongside it. */
  entryReviewStatus?: InvoiceRow['review_status'];
}

export interface ReconcileCandidatesResult {
  vendorId: number;
  candidates: InvoiceLink[];
  transactionSides: TransactionSide[];
}

export interface BatchReconcileCandidatesResult {
  allCandidates: InvoiceLink[];
  candidatesByVendor: Map<number, InvoiceLink[]>;
  /** MAJOR-5: per-vendor ref-miss diagnosis pool (priced candidates + unpriced sides).
   *  Optional so pre-existing callers/tests that build the result literal by hand still
   *  type-check; production always populates it. */
  refLookupByVendor?: Map<number, InvoiceLink[]>;
  familyMap: Map<number, number[]>;
}

export class ReconcileLockError extends Error {
  constructor(direction: string) {
    super(`Reconciliation for "${direction}" is already in progress. Try again later.`);
    this.name = 'ReconcileLockError';
  }
}

// ── InvoiceReconcileService ─────────────────────────────────────────────

export class InvoiceReconcileService {
  readonly dao: InvoiceDao;
  private readonly sb: SupabaseClient;
  private readonly companyService?: CompanyService;
  private readonly reporter: ErrorReporter;

  // B2/A10 — owner-token leases. Each acquire mints a token stored per-direction so
  // release only clears OUR lease (a stale run cannot wipe a fresh owner's lock) and
  // the DB steals a lease only past the TTL (> the longest workflow step).
  private readonly lockTokens = new Map<InvoiceDirection, string>();
  /** TTL for a held reconcile lease, in minutes. Must STRICTLY exceed the max run
   *  wall-clock (Cloud Run caps a step at 900s = 15 min), so 20 min leaves headroom;
   *  a 15-min TTL would equal the ceiling and a max-length run would be stealable at
   *  the boundary. */
  private static readonly LOCK_TTL_MINUTES = 20;

  constructor(
    sb: SupabaseClient,
    invalidate?: InvalidateFn,
    companyService?: CompanyService,
    reporter: ErrorReporter = consoleErrorReporter,
  ) {
    this.sb = sb;
    this.dao = new InvoiceDao(sb, invalidate);
    this.companyService = companyService;
    this.reporter = reporter;
  }

  // --- Locking ---

  /** Acquire reconcile lock for a direction. Returns true if acquired, false if already locked.
   *  Mints an owner token (kept in-memory) so releaseLock only clears our own lease. */
  async acquireLock(direction: InvoiceDirection): Promise<boolean> {
    const token = crypto.randomUUID();
    // `sb` is an untyped SupabaseClient, so rpc().data is `any` — assert the SQL return type.
    const res = await this.sb.rpc('try_reconcile_lock', {
      p_direction: direction,
      p_token: token,
      p_ttl_minutes: InvoiceReconcileService.LOCK_TTL_MINUTES,
    });
    const data = res.data as boolean | null;
    const error = res.error;
    if (error) throw error;
    if (data) this.lockTokens.set(direction, token);
    return !!data;
  }

  /** Release reconcile lock for a direction — only if WE hold it (token-scoped). */
  async releaseLock(direction: InvoiceDirection): Promise<void> {
    const token = this.lockTokens.get(direction) ?? null;
    const { error } = await this.sb.rpc('release_reconcile_lock', {
      p_direction: direction,
      p_token: token,
    });
    if (error) throw error;
    this.lockTokens.delete(direction);
  }

  // --- Single invoice (no family expansion) — for FE ---

  async getReconcileCandidatesFromRow(
    invoice: Pick<InvoiceRow, 'direction' | 'seller_company_id' | 'buyer_company_id'>,
    side?: TransactionSideType,
  ): Promise<ReconcileCandidatesResult> {
    const vendorId = getVendorId(invoice);
    if (vendorId == null) {
      return { vendorId: 0, candidates: [], transactionSides: [] };
    }
    const candidates = await this.dao.getCandidatesForVendor(vendorId, side ? { side } : undefined);
    const transactionSides = candidates.map(candidateToTransactionSide);
    return { vendorId, candidates, transactionSides };
  }

  // --- Single invoice with family expansion ---

  async getReconcileCandidatesWithFamily(
    invoice: Pick<InvoiceRow, 'direction' | 'seller_company_id' | 'buyer_company_id'>,
    side?: TransactionSideType,
  ): Promise<ReconcileCandidatesResult> {
    if (!this.companyService) {
      return this.getReconcileCandidatesFromRow(invoice, side);
    }
    const vendorId = getVendorId(invoice);
    if (vendorId == null) {
      return { vendorId: 0, candidates: [], transactionSides: [] };
    }
    const { expandedIds } = await this.companyService.expandVendorFamily([vendorId]);
    const candidates = await this.dao.getCandidatesForVendors(expandedIds);
    const filtered = side ? filterCandidatesBySide(candidates, side) : candidates;
    const transactionSides = filtered.map(candidateToTransactionSide);
    return { vendorId, candidates: filtered, transactionSides };
  }

  // --- Batch candidate fetching ---

  async getReconcileCandidatesForInvoices(
    invoices: Pick<InvoiceRow, 'direction' | 'seller_company_id' | 'buyer_company_id'>[],
  ): Promise<BatchReconcileCandidatesResult> {
    const empty: BatchReconcileCandidatesResult = {
      allCandidates: [], candidatesByVendor: new Map(), refLookupByVendor: new Map(), familyMap: new Map(),
    };
    if (invoices.length === 0) return empty;

    const vendorIds = [...new Set(
      invoices.map(getVendorId).filter((id): id is number => id != null),
    )];
    if (vendorIds.length === 0) return empty;

    let expandedIds = vendorIds;
    let familyMap = new Map<number, number[]>();
    if (this.companyService) {
      const family = await this.companyService.expandVendorFamily(vendorIds);
      expandedIds = family.expandedIds;
      familyMap = family.familyMap;
    }

    const [allCandidates, unpricedRefSides] = await Promise.all([
      this.dao.getCandidatesForVendors(expandedIds),
      // MAJOR-5: unpriced sides candidate_sides excludes — diagnosis pool only.
      this.dao.getUnpricedRefSidesForVendors(expandedIds),
    ]);

    const candidatesByVendor = new Map<number, InvoiceLink[]>();
    const refLookupByVendor = new Map<number, InvoiceLink[]>();
    for (const vid of vendorIds) {
      const familyIds = new Set(familyMap.get(vid) ?? [vid]);
      const vendorCandidates = allCandidates.filter(c => c.vendor_id != null && familyIds.has(c.vendor_id));
      candidatesByVendor.set(vid, vendorCandidates);
      // Ref-miss diagnosis pool: priced candidates + this vendor's unpriced sides.
      const vendorUnpriced = unpricedRefSides.filter(c => c.vendor_id != null && familyIds.has(c.vendor_id));
      refLookupByVendor.set(vid, [...vendorCandidates, ...vendorUnpriced]);
    }

    return { allCandidates, candidatesByVendor, refLookupByVendor, familyMap };
  }

  // --- Match-item building / result shaping / writing (shared by both batch modes) ---

  /** An invoice's usable candidate pool from `candidate_sides` (which carries ALL sides
   *  with `linked_invoice_id`): a side is usable when it's FREE (`linked_invoice_id` null)
   *  OR already linked to THIS invoice (own → re-confirm on a re-sweep). A side linked to a
   *  DIFFERENT invoice is excluded — so a re-sweep neither double-claims another invoice's
   *  match nor wipes its own. Replaces the free-only view + the own-link merge. */
  private candidatesFor(ctx: ReconcileBatchContext, p: ReconcilePreparedInvoice): InvoiceLink[] {
    const pool = this.poolForVendor(ctx.candidatesByVendor, getVendorId(p.invoice));
    return pool.filter(c => c.linked_invoice_id == null || c.linked_invoice_id === p.invoice.id);
  }

  /** Vendor-keyed pool lookup that tolerates a null vendor id — such invoices never key the map,
   *  so a null vendor yields the same empty pool a missing key would. */
  private poolForVendor<T>(map: Map<number, T[]>, vendorId: number | null): T[] {
    return (vendorId == null ? undefined : map.get(vendorId)) ?? [];
  }

  /** Build matcher line items (non-zero only) from an invoice's effective line items. */
  private buildMatchItems(p: ReconcilePreparedInvoice): MatchLineItem[] {
    const { invoice, effectiveLineItems, originalLineItems } = p;
    const liByOrdinal = new Map(originalLineItems.map(li => [li.ordinal, li]));
    return effectiveLineItems
      .filter(li => Math.abs(li.net_amount ?? 0) >= 0.01)
      .map(li => {
        const orig = liByOrdinal.get(li.ordinal);
        return {
          id: li.ordinal, name: orig?.name ?? null, description: orig?.description ?? null,
          reference_number: orig?.reference_number ?? null,
          quantity: li.quantity, unit: orig?.unit ?? null, unit_price: li.unit_price,
          currency: orig?.currency ?? invoice.currency ?? 'PLN',
          price_type: orig?.price_type ?? null,
          vat_rate_code: li.vat_rate_code,
          net_amount: li.net_amount, vat_amount: li.vat_amount, gross_amount: li.gross_amount,
        };
      }) as MatchLineItem[];
  }

  /** Attach the invoice's KSeF-extracted references (`external_references`) to its match
   *  items by amount (mutates `items[].reference_number` so P1 links them), and return the
   *  line-row updates to persist — resolving the item ordinal to its DB line id. */
  private attachInvoiceReferences(
    p: ReconcilePreparedInvoice,
    items: MatchLineItem[],
    candidates: InvoiceLink[],
  ): Array<{ id: number; reference_number: string }> {
    const assignments = assignReferencesToLines(items, candidates, p.invoice.external_references);
    if (assignments.length === 0) return [];
    const idByOrdinal = new Map(p.originalLineItems.map(li => [li.ordinal, li.id]));
    return assignments
      .map(a => ({ id: idByOrdinal.get(a.lineId), reference_number: a.reference_number }))
      .filter((u): u is { id: number; reference_number: string } => u.id != null);
  }

  /** Shape one invoice's MatchResult (or null = all line items zeroed) into a write entry. */
  private shapeResult(p: ReconcilePreparedInvoice, candidates: InvoiceLink[], matchResult: MatchResult | null): MatchWriteResult {
    const { invoice, originalLineItems, correctionIds } = p;
    // Finding 7: an invoice that entered the sweep pool already approved (arrived
    // approved from Fakturownia, never reconciled) must not be demoted by matching.
    const enteredApproved = invoice.review_status === 'approved';
    // The sweep pulls request_correction invoices (includeRequestCorrection) so a
    // newly-arrived correction can RESOLVE the flag. Re-selecting them is not the no-op
    // that assumed: the matcher only emits 'request_correction' when it holds firm links
    // with mismatching amounts, so an operator-set flag on an unlinkable invoice was
    // rewritten as 'pending' on every sweep. Preserve it — only an 'approved' decision
    // (the correction landed and everything reconciles) may clear it.
    const enteredRequestCorrection = invoice.review_status === 'request_correction';
    const entryReviewStatus = invoice.review_status;
    if (matchResult === null) {
      // All line items zeroed out by corrections — approve with no links. Finding 3: STILL
      // write a (no-op) report so reconcile_report is non-null; otherwise an approved-unswept
      // invoice with nothing to match re-enters the pool (review_status='approved' AND
      // reconcile_report IS NULL) on every sweep, forever.
      return {
        invoiceId: invoice.id,
        correctionIds,
        links: [],
        reviewStatus: 'approved',
        report: buildNoopReconcileReport('All line items zeroed by corrections — nothing to reconcile.'),
        // A request_correction original whose lines are now fully zeroed is exactly the
        // case the flag was waiting for: let 'approved' through, don't preserve.
        preserveReviewStatus: enteredApproved,
        entryReviewStatus,
      };
    }

    const report = buildReconcileReport({
      matchResult,
      invoiceTotals: invoice.net_amount != null
        ? { net: invoice.net_amount, vat: invoice.vat_amount ?? 0, gross: invoice.gross_amount ?? 0 }
        : undefined,
      candidates,
    });

    if (matchResult.links.length === 0) {
      // Approved-unswept or correction-flagged with no links: preserve the entry status
      // (don't demote to pending just because the sweep found nothing to auto-link).
      return {
        invoiceId: invoice.id, correctionIds, links: [], reviewStatus: 'pending', report,
        preserveReviewStatus: enteredApproved || enteredRequestCorrection,
        entryReviewStatus,
      };
    }

    const ordinalToId = new Map(originalLineItems.map(li => [li.ordinal, li.id]));
    const reviewStatus: MatchWriteResult['reviewStatus'] = matchResult.requiresCorrection
      ? 'request_correction'
      : matchResult.status === 'approved' ? 'approved' : 'pending';
    return {
      invoiceId: invoice.id,
      correctionIds,
      // Firm links exist but amounts disagree → flag for a vendor correction (takes
      // precedence over a clean 'approved'; never silently approves wrong amounts).
      reviewStatus,
      // For an already-approved invoice, preserve its status UNLESS matching flagged it
      // for correction — a request_correction is the one signal allowed to override an
      // approved status (per finding 7; finding 4 already excludes our-data-incomplete
      // cases from request_correction). Symmetrically, an entered request_correction is
      // preserved unless matching now approves it.
      preserveReviewStatus: enteredApproved
        ? reviewStatus !== 'request_correction'
        : enteredRequestCorrection && reviewStatus !== 'approved',
      entryReviewStatus,
      report,
      links: matchResult.links.map(link => ({
        invoice_id: invoice.id,
        invoice_line_item_id: (link.invoice_line_item_id != null ? ordinalToId.get(link.invoice_line_item_id) : undefined) ?? link.invoice_line_item_id ?? null,
        transaction_id: link.entity_type === 'transaction' ? link.entity_id : null,
        transaction_transport_id: link.entity_type === 'transport' ? link.entity_id : null,
        side: (link.side as 'load' | 'unload') ?? null,
        stale: false,
      })),
    };
  }

  /** Bulk write of match results — atomic, one round trip via the `reconcile_apply` SQL
   *  function (delete links + insert links + status/report + correction propagation in a
   *  single transaction). A full sweep writes ~2k invoices at once; the old N client-side
   *  calls (delete + insert + per-row updateMany + status bulk) timed out / crashed the
   *  container. A transient deadlock (40P01) against a concurrent writer is still retried. */
  private async writeResults(results: MatchWriteResult[]): Promise<void> {
    if (results.length === 0) return;
    const totalLinks = results.reduce((s, r) => s + r.links.length, 0);
    console.log(`[reconcile:batch] writing ${totalLinks} links for ${results.length} invoices (atomic rpc)`);

    const payload: ReconcileWritePayload = {
      invoices: results.map(r => ({
        id: r.invoiceId,
        review_status: r.reviewStatus,
        report: r.report ?? null,
        preserve_review_status: r.preserveReviewStatus ?? false,
        links: r.links.map(l => ({
          invoice_line_item_id: l.invoice_line_item_id ?? null,
          transaction_id: l.transaction_id ?? null,
          transaction_transport_id: l.transaction_transport_id ?? null,
          side: (l.side as 'load' | 'unload' | null) ?? null,
          stale: l.stale ?? false,
        })),
      })),
      // Corrections are approved when their original ENDS UP approved: either matching
      // approved it, or its preserved entry status was already 'approved'. A preserved
      // 'request_correction' original is NOT approved, so its corrections aren't either.
      approved_correction_ids: results
        .filter(r => (r.preserveReviewStatus ? r.entryReviewStatus === 'approved' : r.reviewStatus === 'approved')
          && r.correctionIds.length > 0)
        .flatMap(r => r.correctionIds),
    };

    const summary = await withDeadlockRetry(() => this.dao.applyReconcileWrite(payload, { invalidate: false }));
    console.log(`[reconcile:batch] write done — atomic reconcile_apply complete`);

    // Finding 4: surface any auto-link inserts the RPC's freshness guard dropped (a
    // concurrent write claimed the side). Never silent — logged always, escalated to Sentry
    // (via the injected reporter) when > 0. The affected invoices were already NOT upgraded
    // to 'approved' inside the RPC (recomputed from what was actually written).
    if (summary && summary.guard_skipped_total > 0) {
      const affected = summary.invoices.filter(i => i.guard_skipped > 0);
      console.warn(`[reconcile:batch] freshness guard dropped ${summary.guard_skipped_total} link insert(s) across ${affected.length} invoice(s):`, affected);
      this.reporter.captureMessage(
        `reconcile_apply dropped ${summary.guard_skipped_total} link insert(s) to a concurrent-write freshness guard`,
        { guard_skipped_total: summary.guard_skipped_total, invoices: affected },
      );
    }
  }

  // --- Standard process batch (match + write) ---

  /** Match and write links for a batch of prepared invoices.
   *
   *  Delegates to {@link globalProcessBatch}. This used to match one invoice at a time,
   *  running the whole P1→P2 ladder for invoice A before invoice B's P1 ever ran — so a
   *  blind subset-sum on A could consume a transaction that B named by reference, leaving
   *  B unlinked and A holding a transaction it never names. The shared used-map did not
   *  help: it only prevents double-claiming, not claiming in the wrong order. Reference
   *  allocation must complete across the WHOLE batch before any amount match starts. */
  async standardProcessBatch(ctx: ReconcileBatchContext): Promise<void> {
    await this.globalProcessBatch(ctx);
  }

  /** Match and write links for ALL prepared invoices in ONE global pass: P1 (reference)
   *  across every invoice first, then P2 (subset-sum) for the rest, sharing one allocation
   *  map. A reference always claims its exact transaction before a blind subset-sum on
   *  another invoice can consume it — the cross-invoice starvation that per-invoice/chunked
   *  processing caused. */
  async globalProcessBatch(ctx: ReconcileBatchContext): Promise<void> {
    console.log(`[reconcile:global] matching ${ctx.prepared.length} invoices (global P1→P2)`);
    const inputs: BatchMatchInput[] = [];
    // Parallel to `inputs` — inputs[i] was built from preparedByInput[i].
    const preparedByInput: ReconcilePreparedInvoice[] = [];
    const zeroed: ReconcilePreparedInvoice[] = [];
    const lineRefUpdates: Array<{ id: number; reference_number: string }> = [];

    for (const p of ctx.prepared) {
      const items = this.buildMatchItems(p);
      if (items.length === 0) { zeroed.push(p); continue; }
      const candidates = this.candidatesFor(ctx, p);
      // Attach the invoice's KSeF references (footer/annotations) to their lines so P1 links them.
      lineRefUpdates.push(...this.attachInvoiceReferences(p, items, candidates));
      preparedByInput.push(p);
      inputs.push({
        invoiceId: p.invoice.id,
        lineItems: items,
        currency: p.invoice.currency || 'PLN',
        candidates,
        sellDate: p.effectiveSellDate,
        allowAdjacentMonth: isInvoicePaid(p.invoice),
        // Full vendor pool for ref-miss diagnosis (finding 6 + MAJOR-5: includes unpriced).
        refLookup: this.poolForVendor(ctx.refLookupByVendor ?? ctx.candidatesByVendor, getVendorId(p.invoice)),
      });
    }

    // Persist newly-attached line references (for the app + subsequent sweeps).
    if (lineRefUpdates.length > 0) await this.dao.updateLineItems(lineRefUpdates, { invalidate: false });

    const matched = matchInvoiceBatch(inputs, new Map());

    const results: MatchWriteResult[] = [];
    for (const p of zeroed) results.push(this.shapeResult(p, [], null));
    for (let i = 0; i < inputs.length; i++) {
      const p = preparedByInput[i];
      // matchInvoiceBatch emits exactly one entry per input, so this never falls back.
      results.push(this.shapeResult(p, this.candidatesFor(ctx, p), matched.get(inputs[i].invoiceId) ?? null));
    }

    console.log(`[reconcile:global] matched ${inputs.length} (+${zeroed.length} zeroed) — writing`);
    await this.writeResults(results);
  }

  /** Auto-reconcile a single invoice. No locking, no touch — direct match + write. */
  async reconcileInvoice(invoiceId: number): Promise<void> {
    const log = (msg: string, data?: unknown) => console.log(`[reconcile:${invoiceId}] ${msg}`, data ?? '');

    log('start — fetching invoice');
    const invoice = await this.dao.getById(invoiceId);
    log(`invoice loaded: ${invoice.invoice_number}, direction=${invoice.direction}, currency=${invoice.currency}`);

    log('preparing (corrections + effective line items)');
    const prepared = await this.prepareForMatching([invoice]);
    const p = prepared[0];
    log(`prepared: ${p.effectiveLineItems.length} effective line items, ${p.correctionIds.length} corrections`, {
      lineItems: p.effectiveLineItems.map(li => ({ ordinal: li.ordinal, net: li.net_amount, zeroed: Math.abs(li.net_amount ?? 0) < 0.01 })),
    });

    log('fetching candidates');
    // candidate_sides carries the invoice's OWN linked sides (linked_invoice_id == this
    // invoice), so a re-reconcile re-confirms them via candidatesFor() — no own-link
    // merge needed (it excludes only sides linked to OTHER invoices).
    const { candidatesByVendor, refLookupByVendor, familyMap } = await this.getReconcileCandidatesForInvoices([invoice]);
    const vendorId = getVendorId(invoice);

    const candidateCount = this.poolForVendor(candidatesByVendor, vendorId).length;
    const familySize = vendorId == null ? undefined : familyMap.get(vendorId)?.length;
    log(`candidates: vendorId=${vendorId}, count=${candidateCount}, familySize=${familySize ?? 1}`);

    log('running standardProcessBatch');
    await this.standardProcessBatch({ prepared, candidatesByVendor, refLookupByVendor, familyMap });
    log('done');
  }

  // --- Prepare for matching (correction merging) ---

  /**
   * Prepare originals for matching: fetch corrections, line items, merge effective line items.
   * Returns PreparedInvoice[] ready for the matching engine.
   */
  async prepareForMatching(originals: InvoiceRow[]): Promise<ReconcilePreparedInvoice[]> {
    if (originals.length === 0) return [];

    const originalIds = originals.map(inv => inv.id);

    // Fetch corrections for these originals
    const corrections = await this.dao.getCorrections(originalIds);
    const correctionIds = corrections.map(c => c.id);

    // Fetch line items for originals + corrections
    const allInvoiceIds = [...originalIds, ...correctionIds];
    const lineItemsByInvoiceId = await this.dao.getLineItemsForInvoices(allInvoiceIds);

    // Group corrections by original ID
    const correctionsByOrigId = new Map<number, InvoiceRow[]>();
    for (const corr of corrections) {
      const origId = corr.correction_of_invoice_id;
      if (origId == null) continue;
      const bucket = correctionsByOrigId.get(origId);
      if (bucket) bucket.push(corr);
      else correctionsByOrigId.set(origId, [corr]);
    }

    // Merge effective line items per original
    return originals.map(inv => {
      const originalLineItems = lineItemsByInvoiceId.get(inv.id) ?? [];
      const invCorrections = correctionsByOrigId.get(inv.id) ?? [];
      const corrLineItems: InvoiceLineRow[] = [];
      const corrIds: number[] = [];
      for (const corr of invCorrections) {
        corrIds.push(corr.id);
        corrLineItems.push(...(lineItemsByInvoiceId.get(corr.id) ?? []));
      }

      // Storno (cancellation): a correction whose HEADER net is ~0 (its corrected
      // total) yet whose line nets MIRROR the original (their sum equals the
      // original's net) cancels the invoice. The per-line net is
      // `total_price_net × sign` (FakturowniaService), so applyCorrections would
      // SUM the mirror onto the original and DOUBLE it (e.g. inv 486 → −3379.28
      // instead of 0). A genuine value-correction's lines are partial deltas that
      // do NOT equal the original. When a storno is present the original is
      // cancelled → empty effective line items → "approved, no links" (its
      // correction is approved via approved_correction_ids).
      const origNet = originalLineItems.reduce((s, li) => s + (li.net_amount ?? 0), 0);
      const isStorno = invCorrections.some(c => {
        const corrNet = (lineItemsByInvoiceId.get(c.id) ?? [])
          .reduce((s, li) => s + (li.net_amount ?? 0), 0);
        return Math.abs(c.net_amount ?? 0) < 0.01      // header: corrected total ~0
          && Math.abs(corrNet) >= 0.01                 // but lines carry value
          && Math.abs(corrNet - origNet) < 0.01;       // mirroring the original → would double
      });

      const effectiveLineItems = isStorno ? [] : applyCorrections(originalLineItems, corrLineItems);
      // A correction can change the sale date (sometimes into a different month).
      // The effective sell date = the latest correction's sell_date (by issue_date),
      // else the original's. The same-month date gate is checked against this.
      const latestCorr = invCorrections
        .filter(c => c.sell_date)
        .sort((a, b) => (a.issue_date ?? '').localeCompare(b.issue_date ?? ''))
        .at(-1);
      const effectiveSellDate = latestCorr?.sell_date ?? inv.sell_date;
      return { invoice: inv, effectiveLineItems, originalLineItems, correctionIds: corrIds, effectiveSellDate };
    });
  }

  /**
   * Full sweep for a direction: hold the reconcile lock and process the ENTIRE pending
   * pool in ONE global pass (P1 across every invoice, then P2), so reference matches claim
   * their exact transactions before any subset-sum and a single allocation map spans the
   * whole pool. At this scale (~1k unmatched lines / ~700 free sides) one pass is
   * milliseconds.
   *
   * No touch bookkeeping: the lock alone serializes concurrent same-direction runs, and a
   * single batch needs no cross-chunk draining. (Touch is a chunked-mode-only mechanism —
   * leaving it on caused processed invoices to be skipped on a follow-up run.) Leftover
   * touches from past chunked runs are ignored via `ignoreTouch`.
   */
  async runFullReconcile(direction: InvoiceDirection): Promise<BatchReconcileResult> {
    const locked = await this.acquireLock(direction);
    if (!locked) throw new ReconcileLockError(direction);
    try {
      const originals = await this.dao.getPendingInvoices(direction, {
        limit: 100_000,
        invoiceType: 'original',
        ignoreTouch: true,
        // Finding 7: also sweep invoices that arrived already-approved from Fakturownia
        // and were never reconciled (reconcile_report IS NULL). They keep their approved
        // status (preserve_review_status) — matching only adds links + a report.
        includeApprovedUnswept: true,
        // A request_correction invoice is otherwise unreachable by any sweep, so the
        // correction that would resolve it never gets applied. See InvoiceDao.
        includeRequestCorrection: true,
      });
      console.log(`[reconcile:full] ${direction}: ${originals.length} pending/approved-unswept originals`);
      if (originals.length === 0) return { totalProcessed: 0, batches: 0, hasMore: false };

      const prepared = await this.prepareForMatching(originals);
      // candidate_sides carries ALL sides with linked_invoice_id; candidatesFor() filters
      // per-invoice to free + own, so a re-sweep re-confirms prior matches (no oscillation)
      // without a separate own-link fetch/merge.
      const { candidatesByVendor, refLookupByVendor, familyMap } = await this.getReconcileCandidatesForInvoices(originals);
      await this.globalProcessBatch({ prepared, candidatesByVendor, refLookupByVendor, familyMap });

      return { totalProcessed: originals.length, batches: 1, hasMore: false };
    } finally {
      await this.releaseLock(direction);
    }
  }

  // --- Batch reconcile orchestration ---

  /**
   * Run batch reconciliation: acquire lock, process invoices in batches, release lock.
   * Fetches only originals, prepares with corrections, then calls processBatch.
   */
  async runBatchReconcile(
    opts: BatchReconcileOptions,
    processBatch: ProcessBatchFn,
  ): Promise<BatchReconcileResult> {
    const batchSize = opts.batchSize ?? 50;
    const locked = await this.acquireLock(opts.direction);
    if (!locked) {
      throw new ReconcileLockError(opts.direction);
    }

    try {
      let totalProcessed = 0;
      let batchCount = 0;
      let hasMore = false;

      while (true) {
        // Fetch next batch of untouched pending originals
        const originals = await this.dao.getPendingInvoices(opts.direction, {
          limit: batchSize,
          invoiceId: opts.invoiceId,
          invoiceType: 'original',
          // Chunked mode: long window so touched invoices stay skipped for the whole
          // looped drain. Unbounded mode keeps the 2-min default (single-call drain).
          touchTimeoutMinutes: opts.maxBatches != null ? CHUNKED_TOUCH_TIMEOUT_MIN : undefined,
          // The shadow workflows reconcile through THIS path (not runFullReconcile),
          // so the approved-unswept backlog must be included here too. Safe: every
          // processed invoice gets a report (no-op included), so the pool
          // self-drains, and shapeResult preserves entry review_status.
          includeApprovedUnswept: true,
          includeRequestCorrection: true,
        });
        if (originals.length === 0) break;

        // Touch before processing — prevents re-fetch in concurrent runs
        const batchIds = originals.map(inv => inv.id);
        await this.dao.touchInvoices(batchIds);

        // Prepare: fetch corrections + line items, merge effective line items
        const prepared = await this.prepareForMatching(originals);

        // Fetch candidates for this batch
        const { candidatesByVendor, refLookupByVendor, familyMap } = await this.getReconcileCandidatesForInvoices(originals);

        // Let the caller do matching + writing. If it throws (e.g. an
        // unrecoverable deadlock after retries), clear the touch for this batch
        // so the next run reprocesses it — otherwise a failed batch stays touched
        // and getPendingInvoices silently skips it forever (the invoice never
        // reconciles even though it has a valid candidate).
        try {
          await processBatch({ prepared, candidatesByVendor, refLookupByVendor, familyMap });
        } catch (err) {
          await this.dao.clearTouch(batchIds);
          throw err;
        }

        // Unbounded mode: clear touch so unmatched invoices are eligible for the next
        // run. Chunked mode (maxBatches set): leave them touched so the next chunk skips
        // them (within the 2-min touch window) and the next run retries them.
        if (opts.maxBatches == null) await this.dao.clearTouch(batchIds);

        totalProcessed += originals.length;
        batchCount++;

        // Single invoice mode — one batch only
        if (opts.invoiceId != null) break;
        // If we got fewer than batchSize, we're done
        if (originals.length < batchSize) break;
        // Chunked mode: stop at the cap; more untouched pending likely remain.
        if (opts.maxBatches != null && batchCount >= opts.maxBatches) { hasMore = true; break; }
      }

      return { totalProcessed, batches: batchCount, hasMore };
    } finally {
      await this.releaseLock(opts.direction);
    }
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────

/** True for a Postgres deadlock (SQLSTATE 40P01 / "deadlock detected"). */
function isDeadlockError(e: unknown): boolean {
  const code = (e as { code?: string } | null | undefined)?.code;
  const msg  = (e as { message?: string } | null | undefined)?.message ?? '';
  return code === '40P01' || /deadlock detected/i.test(String(msg));
}

/**
 * Retry `fn` on a Postgres deadlock (40P01). Used to wrap the idempotent
 * reconcile batch-write block so a transient deadlock against a concurrent
 * writer (the other-direction branch, or an operator reconciling from the FE)
 * is retried rather than aborting the batch. Non-deadlock errors propagate
 * immediately.
 */
async function withDeadlockRetry<T>(fn: () => Promise<T>, attempts = 3): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await fn();
    } catch (e) {
      if (!isDeadlockError(e) || attempt === attempts) throw e;
      lastErr = e;
      console.warn(`[reconcile] deadlock detected — retrying batch write (attempt ${attempt}/${attempts})`);
      await new Promise(resolve => setTimeout(resolve, 50 * attempt));
    }
  }
  throw lastErr; // unreachable — loop either returns or throws
}

function filterCandidatesBySide(candidates: InvoiceLink[], side: TransactionSideType): InvoiceLink[] {
  if (side === 'transport') return candidates.filter(c => c.entity_type === 'transport');
  return candidates.filter(c => c.entity_type === 'transaction' && c.side === side);
}
